import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../utils/api';
import { ArrowRight, Search, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function CategoryArticles() {
  const { categoryId } = useParams();
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadCategory();
  }, [categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => loadArticles(), 300);
    return () => clearTimeout(t);
  }, [categoryId, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategory = async () => {
    try {
      const { data } = await api.get('/categories');
      const cat = data.find(c => c.id === categoryId);
      setCategory(cat);
    } catch (error) {
      console.error('Failed to load category:', error);
    }
  };

  const loadArticles = async () => {
    try {
      const params = new URLSearchParams({ category: categoryId });
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      const { data } = await api.get(`/articles?${params.toString()}`);
      setArticles(data);
    } catch (error) {
      console.error('Failed to load articles:', error);
    }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-16">
        {category && (
          <div className="mb-12">
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              {category.name}
            </h1>
            <p className="text-lg mb-8" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
              {category.description}
            </p>
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#8C867D', strokeWidth: 2 }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles in this category..."
                className="w-full pl-12 pr-12 py-3 rounded-full border transition-all duration-300 focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2A29' }}
                data-testid="category-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                  data-testid="clear-search"
                >
                  <X className="w-4 h-4" style={{ color: '#595553' }} />
                </button>
              )}
            </div>
          </div>
        )}

        {articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="block p-6 rounded-3xl transition-all duration-300 hover:-translate-y-2"
                style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
                data-testid={`article-card-${article.id}`}
              >
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                  {article.title}
                </h3>
                <p className="text-sm mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                  {article.content.replace(/<[^>]+>/g, ' ').slice(0, 150)}...
                </p>
                <div className="flex items-center gap-2" style={{ color: '#E57A44', fontFamily: 'Nunito, sans-serif', fontWeight: 600 }}>
                  <span>Read More</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            {searchQuery ? `No articles matching "${searchQuery}" in this category.` : 'No articles yet in this category.'}
          </p>
        )}
      </div>
      <Footer />
    </div>
  );
}
